import React, { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';
import { formatMoney, formatMoneyRoundWithoutDecimals } from '../../../util/currency';
import { types as sdkTypes } from '../../../util/sdkLoader';

import { IconArrowHead, H4 } from '../../../components';

import ListingCard from '../BrandProductsSection/ListingCard/ListingCard';
import css from './BrandProductsCarousel.module.css';

const { Money } = sdkTypes;

const Swiper =
  typeof window !== 'undefined'
    ? require('swiper/react').Swiper
    : dynamic(() => import('swiper/react'), {
        ssr: false,
      });
const SwiperSlide =
  typeof window !== 'undefined'
    ? require('swiper/react').SwiperSlide
    : dynamic(() => import('swiper/react'), {
        ssr: false,
      });

const BrandProductsCarousel = props => {
  const {
    intl,
    config,
    rentals,
    brandName,
    brandId,
    inProgress,
    pagination,
    onFetchListingsBrandProducts,
  } = props;

  if (!rentals?.length || !window || typeof window === 'undefined') {
    return null;
  }

  useEffect(() => {
    // Load swiper CSS only in the browser
    if (typeof window !== 'undefined') {
      require('swiper/css');
      require('swiper/css/bundle');
    }
  }, []);

  const Navigation =
    typeof window !== 'undefined'
      ? require('swiper/modules').Navigation
      : dynamic(() => import('swiper/modules'), {
          ssr: false,
        });

  const Controller =
    typeof window !== 'undefined'
      ? require('swiper/modules').Controller
      : dynamic(() => import('swiper/modules'), {
          ssr: false,
        });

  const swiperTopRef = useRef(null);
  const swiperBottomRef = useRef(null);
  const [showNav, setShowNav] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [newPage, setNewPage] = useState(1);

  useEffect(() => {
    if (newPage > 1) {
      onFetchListingsBrandProducts(brandId, config, 50, newPage);
    }
  }, [newPage]);

  useEffect(() => {
    if (!swiperTopRef?.current) return;

    const swiper = swiperTopRef?.current;

    const checkNavVisibility = () => {
      const slidesCount = swiper.slides.length;
      const currentSlidesPerView =
        typeof swiper.params.slidesPerView === 'number'
          ? swiper.params.slidesPerView
          : swiper.params.breakpoints &&
            Object.entries(swiper.params.breakpoints)
              .sort((a, b) => +a[0] - +b[0])
              .reduce((acc, [breakpoint, config]) => {
                if (window.innerWidth >= +breakpoint && config.slidesPerView) {
                  acc = config.slidesPerView;
                }
                return acc;
              }, swiper.params.slidesPerView);

      setShowNav(slidesCount > currentSlidesPerView);
    };

    checkNavVisibility();
    window.addEventListener('resize', checkNavVisibility);

    return () => window.removeEventListener('resize', checkNavVisibility);
  }, [rentals]);

  useEffect(() => {
    if (
      swiperTopRef?.current &&
      swiperBottomRef.current &&
      swiperTopRef.current.controller &&
      swiperBottomRef.current.controller
    ) {
      swiperTopRef.current.controller.control = swiperBottomRef.current;
      swiperBottomRef.current.controller.control = swiperTopRef.current;
    }
  }, []);

  useEffect(() => {
    if (swiperTopRef.current && swiperBottomRef.current && !inProgress) {
      swiperTopRef.current.slideTo(currentIndex);
      swiperBottomRef.current.slideTo(currentIndex);
    }
  }, [rentals]);

  const handleNextClick = () => {
    if (inProgress) return;

    const swiperTop = swiperTopRef?.current;
    const swiperBottom = swiperBottomRef?.current;

    if (!swiperTop || !swiperBottom) return;

    const index = swiperTop.activeIndex;
    setCurrentIndex(index);

    swiperTop.slideTo(index);
    swiperBottom.slideTo(index);

    if (swiperTop?.isEnd && pagination?.totalPages !== pagination?.page) {
      setNewPage(pagination?.page + 1);
    }
  };

  const handleSlideChange = swiper => {
    const index = swiper.activeIndex;
    setCurrentIndex(index);

    if (swiper.isEnd && pagination?.totalPages !== pagination?.page) {
      setNewPage(pagination?.page + 1);
    }
  };

  return (
    <>
      <div className={css.swiperWrapper}>
        <div className={css.heading} style={{ marginBottom: 20 }}>
          <H4 as="h1" className={css.orderPanelTitle}>
            <FormattedMessage id="BrandProductsCarousel.title" />
          </H4>
        </div>

        <div className={css.displaysContainer}>
          <button
            className={classNames(css.navLeft, { [css.hidden]: !showNav })}
            disabled={!showNav}
          >
            <div className={css.navArrowWrapper}>
              <IconArrowHead direction="left" size="big" />
            </div>
          </button>

          <button
            className={classNames(css.navRight, { [css.hidden]: !showNav })}
            disabled={!showNav}
            onClick={handleNextClick}
          >
            <div className={css.navArrowWrapper}>
              <IconArrowHead direction="right" size="big" />
            </div>
          </button>

          <Swiper
            onSwiper={swiperInstance => {
              swiperTopRef.current = swiperInstance;
            }}
            onSlideChange={handleSlideChange}
            className={css.swiperForListings}
            spaceBetween={20}
            navigation={{
              nextEl: `.${css.navRight}`,
              prevEl: `.${css.navLeft}`,
            }}
            modules={[Navigation, Controller]}
            breakpoints={{
              1200: {
                slidesPerView: 5,
              },
              992: {
                slidesPerView: 5,
              },
              768: {
                slidesPerView: 4,
              },
              576: {
                slidesPerView: 3,
              },
              0: {
                slidesPerView: 2,
              },
            }}
          >
            {rentals.map((l, i) => {
              return (
                <SwiperSlide
                  key={`${l.id.uuid}-top-${i}`}
                  className={classNames(css.slide, css.slideImage)}
                >
                  <ListingCard listing={l} showPriceInfo={true} />
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
        <Swiper
          onSwiper={swiperInstance => {
            swiperBottomRef.current = swiperInstance;
          }}
          className={css.swiperForListings}
          spaceBetween={20}
          navigation={{
            nextEl: `.${css.navRight}`,
            prevEl: `.${css.navLeft}`,
          }}
          modules={[Controller]}
          breakpoints={{
            1200: {
              slidesPerView: 5,
            },
            992: {
              slidesPerView: 5,
            },
            768: {
              slidesPerView: 4,
            },
            576: {
              slidesPerView: 3,
            },
            0: {
              slidesPerView: 2,
            },
          }}
        >
          {rentals.map((l, i) => {
            const { title = '', publicData } = l.attributes;
            const { discountedPrice, discount } = publicData;

            const discountedPriceMaybe = discountedPrice
              ? formatMoney(
                  intl,
                  new Money(discountedPrice.amount, discountedPrice.currency)
                )
              : null;

            const formattedPrice = l?.attributes?.price
              ? formatMoney(intl, l.attributes.price)
              : null;

            return (
              <SwiperSlide
                key={`${l.id.uuid}-bottom-${i}`}
                className={classNames(css.slide, css.slideText)}
              >
                <div className={css.info}>
                  <p className={css.brandTitle}>{brandName}</p>
                  <p className={css.title}>{title}</p>
                  <div className={css.priceContainer}>
                    {discountedPriceMaybe ? (
                      <>
                        <span className={css.priceDiscounted}>{formattedPrice}</span>
                        <span className={css.discount}>{discount + '%'}</span>
                        <span className={css.priceBeforeDiscount}>{discountedPriceMaybe}</span>
                      </>
                    ) : (
                      <>
                        <span className={css.price}>{formattedPrice}</span>
                      </>
                    )}
                  </div>
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    </>
  );
};

export default BrandProductsCarousel;
